//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExpress Imports

import DevExpress from 'devextreme'
import DataSource from 'devextreme/data/data_source'

//#endregion

import { i18n } from './../i18n/common.i18n'

import {
    ColumnModel,
    FieldNameValueModel
} from '../../api/api.models'

import { CreateEditPopupOptions } from '../../common/create-edit-popup/create-edit-popup.directive'
import { ImportService, ImportErrorsSummaryService, ImportDataService, ImportGridService } from './services'

import { DataTypeService, DataTypeFormatService, DataTypeFormatter } from '../../../services/data-type'

import { CommonHelper, DataTypeClass } from '../../../helpers/common-helper'
import { ConvertHelper } from '../../../helpers/convert-helper'
import { Dictionary, KeyType } from '../../../helpers/dictionary'
import { EventsNames } from '../../../helpers/events-names'
import { IUserSession } from '../../../helpers/user-session'
import { LocalizationHelper } from '../../../helpers/localization-helper'
import { Md5 } from '../../../helpers/md5-helper'
import { ToastHelper } from '../../../helpers/toast-helper'
import { TypeHelper } from '../../../helpers/type-helper'

import { Enums } from '../../../models/common/enums'
import { OpDataType } from '../../../models/core/op-data-type'
import { OpDataTypeInGroup } from '../../../models/core/op-data-type-in-group'
import { OpFile } from '../../../models/core/op-file'
import { OpImportPlugin, OpImportPluginParam, OpImportProgressStep, OpImportSummary, OpImportData, OpImportItemSummary } from '../../../models/util'
import { OperatorLayoutSetting } from '../../../models/local/operator-layout-setting'

import { CommonCreateEdit, CreateEditSection } from '../common.create-edit'
import { CreateEditInitParams } from '../create-edit-popup/create-edit-popup.controller'
import { FileUploaderOptions } from '../file-uploader/file-uploader.directive'
import { GridOptions, GridTypes } from '../grid/grid.controller'

import { ImportOptions } from './import.directive'

import { ConfirmationOptions } from '../confirmation-popup/confirmation-popup.directive'
import { FileComponent } from '../../../helpers/file-component'
import { IIMRErrorsSummary, IMRErrorsSummaryOptions, SummaryOptions, ErrorsSummaryResult } from '../errors-summary'
import { IMRBaseControlDirectiveController } from '../base'


export const COMPONENT_NAME: string = 'import'

interface ImportScope extends ng.IScope {
    gridId?: string

    dataGrid: DevExpress.ui.dxDataGrid
    dataSource: DataSource

    popupName: string

    popoverVisible: boolean
    gridPopupVisible: boolean
    importPopupVisible: boolean

    gridPopupOptions: DevExpress.ui.dxPopupOptions
    importPopupOptions: DevExpress.ui.dxPopupOptions
    backgroundImportConfirmedButtonOptions: DevExpress.ui.dxButtonOptions
    backgroundImportDeclinedButtonOptions: DevExpress.ui.dxButtonOptions

    configurationPopoverOptions: DevExpress.ui.dxPopoverOptions
    configurationPopover: DevExpress.ui.dxPopover

    pluginSelectBoxOptions: DevExpress.ui.dxSelectBoxOptions

    initButtonOptions: DevExpress.ui.dxButtonOptions
    startButtonOptions: DevExpress.ui.dxButtonOptions
    stopButtonOptions: DevExpress.ui.dxButtonOptions
    finishButtonOptions: DevExpress.ui.dxButtonOptions
    startImportWithoutExtensionOptions: DevExpress.ui.dxButtonOptions
    summaryToastOptions: DevExpress.ui.dxToastOptions
    summaryToast: DevExpress.ui.dxToast
    importProgressOptions: DevExpress.ui.dxProgressBarOptions

    fileUploaderOptions: FileUploaderOptions
    gridOptions: GridOptions

    showGrid: boolean

    guid: string
    uploadedPercent: number

    isValidated: boolean
    isStarted: boolean
    isSuccedded: boolean
    isCanceled: boolean
    isUploaded: boolean
    isLoaded: boolean

    isInitButtonDisabled: () => boolean
    isStartButtonDisabled: () => boolean
    isStopButtonDisabled: () => boolean

    columns: ColumnModel[]
    options: ImportOptions

    currentProgressStep: OpImportProgressStep

    filteredPlugins: OpImportPlugin[]

    differentFormat: boolean

    paramsOptions: CreateEditPopupOptions
    paramsItems: any

    needConfirmation: boolean
    confirmationPopupId: number
    confirmResumeTitle: string
    fileUploaderVisible: boolean
    needHideGrid: boolean
    needStopConfirmation: boolean
    stopConfirmationPopupId: number
    isExtensionRequired: boolean

    errorsSummaryVisible: boolean
    errorsSummaryOptions: IMRErrorsSummaryOptions
}

export class ImportController extends IMRBaseControlDirectiveController {

    // Consts

    private static get LOG_STRING(): string { return '[SIMAX.Common.Import.Controller] - %s' }

    private static get REFRESH_PERIOD(): number { return 3000 }

    private static get SUMMARY_MASTER_DETAIL_NAME(): string { return "__ImportItemSummary" }

    private static get ERROR_MARKER(): string { return '*' }

    // Consts End


    // Members

    timeout
    createGridPromise = $.Deferred<void>()

    private importParamsDeferred: JQueryDeferred<OpImportPluginParam[]> = $.Deferred<OpImportPluginParam[]>()

    private isBackgroundImport: boolean = false
    private isFromBackgroundPopover: boolean = false

    private importData: OpImportData
    private importGridDataSource: any[]
    private headerColumns: OpDataTypeInGroup[]
    private selectedPlugin: OpImportPlugin

    private importFailed: boolean = false

    private importPopup: DevExpress.ui.dxPopup = null
    private progressBar: DevExpress.ui.dxProgressBar = null

    private errorsSummaryComponent: IIMRErrorsSummary

    private importStartTime: number
    private importEndTime: number
    private importSummaryResult: OpImportItemSummary[]

    private headerDataTypeDict: Dictionary<OpDataType>

    // Members End


    // Localization Consts

    private get LOCALIZATION_COLLECTION_NAME() { return 'Popup' }

    private localizationText = class {
        public static get Progress() { return 'Progress' }
    }

    private getLocalizedText(text: string): string {
        return LocalizationHelper.getLocalizedText(text, { collection: this.LOCALIZATION_COLLECTION_NAME })
    }

    // Localization Consts End

    private previousFileMd5: string
    private fileReader: FileReader
    private fileName: string


    constructor(
        private $scope: ImportScope,
        private $q: ng.IQService,
        private $timeout: ng.ITimeoutService,
        private userSession: IUserSession,
        private dataTypeService: DataTypeService,
        private dataTypeFormatService: DataTypeFormatService,
        private importService: ImportService,
        private importErrorsSummaryService: ImportErrorsSummaryService,
        private importDataService: ImportDataService,
        private importGridService: ImportGridService
    ) {
        super()

        if (!$scope.gridId) {
            $scope.gridId = ''
        }
        $scope.confirmationPopupId = new Date().getTime()
        $scope.needStopConfirmation = false
        $scope.stopConfirmationPopupId = new Date().getTime() + 10
        $scope.popoverVisible = false
        $scope.gridPopupVisible = false
        $scope.importPopupVisible = false
        $scope.fileUploaderVisible = true
        $scope.isCanceled = false
        $scope.isStarted = false
        $scope.isLoaded = false
        $scope.uploadedPercent = 0
        $scope.currentProgressStep = null

        // SelectedPlugin
        const plugins: OpImportPlugin[] = userSession.getOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.ImportPlugins), null)

        $scope.filteredPlugins = _.filter(plugins, plugin => plugin.type == $scope.options.referenceType)

        if ($scope.filteredPlugins && $scope.filteredPlugins.length > 0) {
            this.selectedPlugin = $scope.filteredPlugins[0]
        } else {
            this.selectedPlugin = new OpImportPlugin()
            console.log("Missing import plugins in OperatorLayourSettings")
        }

        if (this.selectedPlugin.allowedFileExtension == null || this.selectedPlugin.allowedFileExtension.length == 0) {
            this.$scope.popupName = $scope.options.popupName
            $scope.isExtensionRequired = false
        } else {
            $scope.popupName = $scope.options.popupName + (' (' + this.selectedPlugin.allowedFileExtension.join(', ') + ')')
            $scope.isExtensionRequired = true
        }

        $scope.isInitButtonDisabled = () => { return !this.$scope.isUploaded || !this.$scope.isLoaded || $scope.isValidated || $scope.isSuccedded || $scope.isCanceled }
        $scope.isStartButtonDisabled = () => { return !$scope.isValidated || $scope.isStarted || $scope.isCanceled }
        $scope.isStopButtonDisabled = () => { return !$scope.isStarted || $scope.isCanceled }

        this.setCreateEditImportOptions()

        $scope.gridPopupOptions = {
            showTitle: true,
            showCloseButton: true,
            fullScreen: true,
            bindingOptions: {
                visible: 'gridPopupVisible',
                title: 'popupName'
            },
            onInitialized: (e: any): void => { this.onImportPopupInitialized(e) },
            onShowing: () => { this.onGridPopupOptionsShowing() },
            onHiding: (e: any): void => { this.onGridPopupOptionsHiding(e) },
            onHidden: (e: any): void => { this.onGridPopupOptionsHiden(e) }
        }

        $scope.importPopupOptions = {
            width: 400,
            height: 300,
            showTitle: true,
            visible: true,
            bindingOptions: {
                visible: 'importPopupVisible',
                title: 'popupName'
            },
            onHiding: () => { this.onImportPopupHiding() },
            onShowing: () => { this.onImportPopupShowing() }
        }

        $scope.errorsSummaryVisible = false
        this.initializeErrorsSummary()

        this.setSummaryToastOptions()
        this.setPluginFileUploaderPopupOptions()
        this.setProgressBarOptions()
        this.setButtonsOptions()
        this.setPluginFileUploaderOptions()
        this.setEvents()
    }

    setEvents(): void {
        this.$scope.$on(EventsNames.OPEN_IMPORT_POPUP + this.$scope.gridId, (event: ng.IAngularEvent, ...args: any[]) => { this.onImportPopupOpen(args) })
        this.$scope.$on(EventsNames.CLOSE_IMPORT_POPUP, (event: ng.IAngularEvent, ...args: any[]) => { this.onImportPopupClose(args) })
    }

    onImportPopupOpen(args: any[]): void {
        if (args[0]) {
            this.$scope.importPopupVisible = !this.$scope.importPopupVisible
        } else {
            this.$scope.popoverVisible = !this.$scope.popoverVisible
        }

        if (!this.$scope.popoverVisible) {
            $('.dx-button').blur()
        } else {
            if (this.$scope.filteredPlugins && this.$scope.filteredPlugins.length == 1) {
                this.$scope.fileUploaderOptions.disabled = true
                this.isPreviousImportCompleted()
            }
        }
    }
    onImportPopupShowing(): void {
        this.$scope.fileUploaderOptions.uploaderVisible = true
        //this.previousStartFileMd5 = ""
        //this.previousStopFileMd5 = ""
    }
    onImportPopupClose(args: any[]) {
        this.$scope.popoverVisible = false
        this.$scope.importPopupVisible = false
    }
    onImportPopupHiding(): void {
        this.$scope.importPopupVisible = false
    }


    // Popup

    private closeImportPopup(): void {
        this.importPopup.hide()
        this.hideErrorsSummarySection()
    }

    private onImportPopupInitialized(e: any): void {
        this.importPopup = e.component
    }

    private onGridPopupOptionsShowing(): void {
        this.$scope.isSuccedded = false
        this.isBackgroundImport = false
        this.isFromBackgroundPopover = false
    }

    private onGridPopupOptionsHiding(e: any): void {
        e.cancel = true

        if (this.$scope.isStarted && !this.isFromBackgroundPopover) {
            this.openBackgroundImportConfirmationPopup()
        } else {
            this.isFromBackgroundPopover = false
            e.cancel = false
        }
    }

    private onGridPopupOptionsHiden(e: any): void {
        if (this.$scope.isStarted && !this.isBackgroundImport) {
            this.stopImport(false)
        }

        this.hideErrorsSummarySection()

        this.$scope.isUploaded = false

        this.stopRefreshTimer()
        this.changeImportStatus(Enums.ImportStatus.Unknown)

        this.$scope.$emit(EventsNames.CLOSED_IMPORT_POPUP, this.$scope.isUploaded)
    }

    // Popup End


    // Grid

    onGridRowPrepared(e: any): void {
        if (e.rowType == "data") {
            const row: JQuery = $(e.rowElement)
            if (e.data.success) {
                row.css('background-color', '#02E6A1')
                row.children('td').css('background-color', 'inherit')
            } else if (e.data.error) {
                row.css('background-color', '#F7484E')
                row.children('td').css('background-color', 'inherit')
            } else if (e.data.skipped) {
                row.css('background-color', '#87CEFA')
                row.children('td').css('background-color', 'inherit')
            }
        }
    }
    onGridCellPrepared(e: any): void {
        if (e.column && e.column.command == 'edit') {
            const el: JQuery = e.cellElement.find('.dx-link:contains("' + LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Delete) + '")')
            if (el.length > 0) {
                el.remove()
                e.cellElement.append('<a class="dx-link" id="link' + e.rowIndex + '">' + LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Delete) + '</a>').click((element) => {
                    const div: string = '<div class="confirmation-popover"></div>'
                    if (e.cellElement.children('.confirmation-popover').length == 0) {
                        e.cellElement.append(div)
                    }

                    let popover: DevExpress.ui.dxPopover

                    e.cellElement.children('.confirmation-popover').dxPopover(<DevExpress.ui.dxPopoverOptions>{
                        target: '#link' + e.rowIndex,
                        visible: true,
                        width: 200,
                        position: 'left',
                        onInitialized: e => {
                            popover = e.component
                        },
                        contentTemplate: contentElement => {
                            if (contentElement.children('.confirmation-message').length == 0) {
                                contentElement.append('<div class="confirmation-message">' + LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ConfirmDeleteRow) + '</div>')
                                contentElement.append('<div class="confirmation-buttons"><div class="yes-button"></div><div class="no-button"></div></div>')

                                contentElement.find('.yes-button').dxButton(<DevExpress.ui.dxButtonOptions>{
                                    text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Yes),
                                    onClick: () => {
                                        const rowIndex: number = this.$scope.dataGrid.getRowIndexByKey(e.key)
                                        this.$scope.dataGrid.deleteRow(rowIndex)
                                    }
                                })

                                contentElement.find('.no-button').dxButton(<DevExpress.ui.dxButtonOptions>{
                                    text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.No),
                                    onClick: () => {
                                        popover.option('visible', false)
                                    }
                                })
                            }
                        }
                    })
                })
            }
        }
    }
    onGridInitialized(e: any): void {
        this.$scope.dataGrid = e.component

        if (this.$scope.currentProgressStep) {
            this.changeImportStatus(Enums.ImportStatus.Loaded)
        }

        this.createGridPromise.resolve()
    }

    // Grid End


    // Import Flow Methods

    private isPreviousImportCompleted(): void {
        this.userSession
            .getDataProvider().System
            .importIsImportCompleted(this.selectedPlugin.type, this.selectedPlugin.name)
            .then((result: boolean) => {
                if (result) {
                    this.enableConfigurationPopover()
                } else {
                    this.openResumeImportConfirmationPopup()
                }
            })
            .fail((error: any) => {
                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)

                ToastHelper.showError(errorMsg || i18n('Popup', 'ErrorISCompleted'))

                console.warn(ImportController.LOG_STRING, "Unable to check if import is completed.", errorMsg)
            })
    }

    private initImport(fileReader: FileReader, fileName: string, importParamsMode?: Enums.ImportInputParamsMode): JQueryPromise<void> {
        this.fileReader = fileReader
        this.fileName = fileName

        const currentMd5: string = Md5.init(fileReader)

        if (this.previousFileMd5 !== currentMd5 || this.importFailed) {
            this.previousFileMd5 = currentMd5
            this.$scope.showGrid = false
        }

        this.importData = null
        this.importParamsDeferred = $.Deferred<OpImportPluginParam[]>()

        if (
            this.selectedPlugin != null
            && _.size(this.selectedPlugin.params) > 0
            && (importParamsMode != null && importParamsMode === this.importService.getImportInputParamsMode())
        ) {
            this.getDataTypeInGroupFromImportParams(this.selectedPlugin.params)
                .then((dataTypeInGroup: OpDataTypeInGroup[]) => {
                    if (dataTypeInGroup != null) {
                        const createEditOptions: CreateEditInitParams = this.getCreateEditOptionsFromDataTypeInGroups(dataTypeInGroup)

                        this.showParamsPopup(createEditOptions)
                    } else {
                        console.warn(ImportController.LOG_STRING, "Unable to load dataTypeInGroup from import params.")

                        this.importParamsDeferred.resolve(null)
                    }
                })
        } else {
            this.importParamsDeferred.resolve(null)
        }

        return this.importParamsDeferred.then((pluginParams: OpImportPluginParam[]) => {
            const dataToImport: OpImportData = this.createOpImportData(fileReader, fileName, pluginParams)

            return this.userSession
                .getDataProvider().System
                .importInitialize(this.selectedPlugin.type, dataToImport, this.selectedPlugin.name)
                .then((importGuid: string) => {
                    return this.importGetData(importGuid)
                })
                .fail((error: any) => {
                    const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)

                    ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.PluginNotConfigured))

                    console.warn(ImportController.LOG_STRING, "Unable to initialize data.", errorMsg)

                    this.enableConfigurationPopover()
                })
                .always(() => {
                    this.$scope.needHideGrid = false
                })
        })
    }

    private importGetData(importGuid: string): JQueryPromise<void> {
        this.$scope.guid = importGuid
        this.$scope.isUploaded = true

        return this.userSession
            .getDataProvider().System
            .importGetData(this.selectedPlugin.type, this.$scope.guid)
            .then((importData: OpImportData) => {
                this.showPopup()

                return this.importDataService
                    .getImportHeaderDataTypes(this.$scope.options.moduleName, importData)
                    .then((headerDataTypeInGroup: OpDataTypeInGroup[]) => {
                        const headerIdDataTypes: number[] = _.map(headerDataTypeInGroup, (dataTypeInGroup: OpDataTypeInGroup) => {
                            return dataTypeInGroup.idDataType
                        })

                        return $
                            .when(
                                this.dataTypeFormatService.getDataTypeFormatter(headerIdDataTypes),
                                this.importDataService.createHeaderDataTypeDict(importData, headerDataTypeInGroup)
                            )
                            .then((dataTypeFormatter: DataTypeFormatter, headerDataTypeDict: Dictionary<OpDataType>) => {
                                this.headerColumns = headerDataTypeInGroup
                                this.headerDataTypeDict = headerDataTypeDict
                                this.$scope.differentFormat = false
                                this.importDataService.prepareImportData(importData, headerDataTypeDict, dataTypeFormatter)
                                this.createGrid(importData, headerDataTypeInGroup, headerDataTypeDict)
                                this.importData = importData
                                this.$scope.showGrid = true
                                this.importFailed = false

                                let currentImportProgressStep = new OpImportProgressStep()
                                let itemCount: number = 0

                                if (this.importData != null && this.importData.data != null) {
                                    itemCount = this.importData.data.length
                                }

                                currentImportProgressStep.itemsCount = itemCount

                                this.$scope.currentProgressStep = currentImportProgressStep

                                this.updateProgressBar(0)
                                this.changeImportState(this.$scope.currentProgressStep)
                            })
                    })
            })
            .fail((error: any) => {
                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)

                ToastHelper.showError(errorMsg || i18n('Popup', 'ServerErrorImport'))

                console.warn(ImportController.LOG_STRING, "Unable to import get data.", errorMsg)

                this.importFailed = true
                this.$scope.showGrid = true
            })
    }

    private initializeAndValidateImport(): void {
        this.initImport(
            this.fileReader,
            this.fileName,
            Enums.ImportInputParamsMode.BeforeValidation
        )
            .then(() => {
                setTimeout(() => { //need to wait until front update button states
                    this.validateImport()
                }, 500)
            })
    }

    private validateImport(): JQueryPromise<void> {

        return this.userSession
            .getDataProvider().System
            .importValidateData(this.selectedPlugin.type, this.$scope.guid)
            .then((result: OpImportItemSummary[]) => {
                this.updateProgressBar(0)

                let currentImportProgressStep: OpImportProgressStep

                if (this.$scope.currentProgressStep) {
                    currentImportProgressStep = this.$scope.currentProgressStep
                } else {
                    currentImportProgressStep = new OpImportProgressStep()

                    currentImportProgressStep.itemsCount = (this.importData && this.importData.data ? this.importData.data.length : 0)
                    currentImportProgressStep.proceedItemsCount = 0
                    currentImportProgressStep.proceedErrorItemsCount = 0
                    currentImportProgressStep.proceedSkippedItemsCount = 0
                    currentImportProgressStep.proceedSuccessItemsCount = 0
                    currentImportProgressStep.proceedWarningItemsCount = 0

                    this.$scope.currentProgressStep = currentImportProgressStep
                }

                let infoText: string = TypeHelper.emptyString
                let hasGridErrors: boolean = false

                result.forEach((info: OpImportItemSummary) => {
                    if (info.importStatus == Enums.ImportStatus.NotValid || info.importStatus == Enums.ImportStatus.Failed) {
                        if (info.key == null) {
                            infoText += info.description
                        } else {
                            hasGridErrors = true
                            this.importData.data[info.key][ImportController.SUMMARY_MASTER_DETAIL_NAME] = info
                        }
                    }
                })

                if (hasGridErrors) {
                    this.prepareDataSource(this.importData, this.headerColumns, this.importGridDataSource, this.headerDataTypeDict)
                    this.$scope.dataGrid.refresh()
                }

                if (infoText.length > 0) {
                    ToastHelper.showWarning(infoText)

                    this.changeImportStatus(Enums.ImportStatus.NotValid)
                } else {
                    ToastHelper.showSuccess(i18n('Popup', 'ImportDataValidated'))

                    this.changeImportStatus(Enums.ImportStatus.Validated)
                }
            })
            .fail((error: any) => {
                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)

                ToastHelper.showError(errorMsg || i18n('Popup', 'ErrorContactWithAdmin'))

                console.warn(ImportController.LOG_STRING, "Unable to validate data.", errorMsg)
            })
    }

    private startImport(): void {
        this.importStartTime = performance.now()
        this.updateProgressBar(0)

        this.userSession
            .getDataProvider().System
            .importStartImport(this.selectedPlugin.type, this.$scope.guid)
            .then((result: Enums.ImportStatus) => {
                this.changeImportStatus(Enums.ImportStatus.InProgress)
                this.importProgress()
            })
            .fail((error: any) => {
                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)

                ToastHelper.showError(errorMsg || i18n('Popup', 'ErrorContactWithAdmin'))

                console.warn(ImportController.LOG_STRING, "Unable to start import.", errorMsg)

                this.changeImportStatus(Enums.ImportStatus.Failed)
            })
    }

    private stopImport(showSummary: boolean = false): void {
        this.userSession
            .getDataProvider().System
            .importStopImport(this.selectedPlugin.type, this.$scope.guid)
            .then(() => {
                this.stopRefreshTimer()
                this.changeImportStatus(Enums.ImportStatus.Cancelled)

                if (showSummary) {
                    this.importSummary()
                }
            })
            .fail((error: any) => {
                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)

                ToastHelper.showError(errorMsg || i18n('Popup', 'ErrorContactWithAdmin'))

                console.warn(ImportController.LOG_STRING, "Unable to stop import.", errorMsg)

                this.changeImportStatus(Enums.ImportStatus.Cancelled)
            })
    }

    private importResume(previouslyCanceled: boolean = false): void {
        this.userSession
            .getDataProvider().System
            .importResume(this.selectedPlugin.type, this.selectedPlugin.name)
            .then((result: OpImportSummary) => {
                this.showPopup()
                this.$scope.needHideGrid = true

                if (OpImportData.isType(result.importObject)) {
                    this.importData = <OpImportData>result.importObject

                    if (this.importData.data && this.importData.headers) {
                        this.importDataService
                            .getImportHeaderDataTypes(this.$scope.options.moduleName, this.importData)
                            .then((headerDataTypeInGroup: OpDataTypeInGroup[]) => {
                                this.headerColumns = headerDataTypeInGroup

                                return this.importDataService.createHeaderDataTypeDict(this.importData, headerDataTypeInGroup)
                            })
                            .then((headerDataTypeDict: Dictionary<OpDataType>) => {
                                this.headerDataTypeDict = headerDataTypeDict
                                this.$scope.guid = result.guid

                                if (previouslyCanceled && this.importData && this.headerColumns && this.importGridDataSource) {
                                    this.prepareDataSource(this.importData, this.headerColumns, this.importGridDataSource, this.headerDataTypeDict)
                                    this.$scope.dataGrid.refresh()
                                    this.importProgress()

                                    this.$scope.needHideGrid = false
                                } else {
                                    this.createGrid(this.importData, this.headerColumns, this.headerDataTypeDict)

                                    this.createGridPromise.then(() => {
                                        this.$scope.dataGrid.refresh()
                                        this.importProgress()
                                        this.$scope.needHideGrid = false
                                    })
                                }
                            })
                    }
                } else {
                    console.log('Unsupported importObject type')
                }
            })
            .fail((error: any) => {
                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)

                ToastHelper.showError(errorMsg || i18n('Popup', 'ErrorContactWithAdmin'))

                console.warn(ImportController.LOG_STRING, "Unable to resume import.", errorMsg)

                this.headerColumns = null
                this.importData = null
                this.importGridDataSource = null
                this.$scope.needHideGrid = true
            })
    }

    private importProgress(): void {
        this.userSession
            .getDataProvider().System
            .importProgress(this.selectedPlugin.type, this.$scope.guid)
            .then((result: OpImportProgressStep) => {
                this.$scope.currentProgressStep = result
                this.$scope.uploadedPercent = Math.round(result.proceedItemsCount / result.itemsCount * 100) || 0

                this.changeImportState(this.$scope.currentProgressStep)

                switch (result.importStatus) {
                    case Enums.ImportStatus.Failed:
                        this.stopRefreshTimer()
                        this.importSummary()

                        break

                    case Enums.ImportStatus.InProgress:
                        if (this.$scope.isStarted) {
                            this.startRefreshTimer()
                        }

                        break

                    case Enums.ImportStatus.Succeded:
                        this.importSummary()

                        break

                    case Enums.ImportStatus.NotStarted:
                    case Enums.ImportStatus.NotValid:
                    case Enums.ImportStatus.Cancelled:
                    default:
                        break
                }
            })
            .fail((error: any) => {
                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)

                ToastHelper.showError(errorMsg || i18n('Popup', 'ErrorContactWithAdmin'))

                console.warn(ImportController.LOG_STRING, "Unable to check import progress.", errorMsg)
            })
    }

    private importSummary(): void {
        this.userSession
            .getDataProvider().System
            .importSummary(this.selectedPlugin.type, this.$scope.guid)
            .then((result: OpImportItemSummary[]) => {
                if (result == null || result.length == 0 || !this.importData) {
                    ToastHelper.showError(i18n('Popup', 'ErrorContactWithAdmin'))

                    this.changeImportStatus(Enums.ImportStatus.Failed)
                } else {
                    this.showErrorsSummarySection()
                    this.importSummaryResult = result

                    result.forEach((stat) => {
                        if (typeof stat.key !== 'undefined' && stat.key != null) {
                            this.importData.data[stat.key][ImportController.SUMMARY_MASTER_DETAIL_NAME] = stat
                        } else if (stat.key == null) {
                            ToastHelper.showError(stat.description)
                        }
                    })

                    this.prepareDataSource(this.importData, this.headerColumns, this.importGridDataSource, this.headerDataTypeDict)
                    this.$scope.dataGrid.refresh()
                    this.importEndTime = performance.now()
                    this.setSummaryOptions({
                        parameters: this.importErrorsSummaryService.createErrorsSummaryParameters(this.$scope.currentProgressStep),
                        title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ImportSummary),
                        duration: this.importEndTime - this.importStartTime,
                        summaryResult: this.importSummaryResult.map((result: OpImportItemSummary): ErrorsSummaryResult => {
                            const status: string = LocalizationHelper.getLocalizedEnum<Enums.ImportStatus>(Enums.ImportStatus, result.importStatus)
                            return {
                                key: result.key,
                                isVisible: true,
                                description: result.description,
                                status: status
                            }
                        }),
                        errorMarker: ImportController.ERROR_MARKER
                    })
                }
            })
            .fail((error: any) => {
                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)

                ToastHelper.showError(errorMsg || i18n('Popup', 'ErrorContactWithAdmin'))

                console.warn(ImportController.LOG_STRING, "Unable to get import summary.", errorMsg)

                this.changeImportStatus(Enums.ImportStatus.Failed)
            })
    }



    // Import Flow Methods End


    // Import Control

    private changeImportStatus(importStatus: Enums.ImportStatus) {
        if (this.$scope.currentProgressStep != null) {
            this.$scope.currentProgressStep.importStatus = importStatus
        }

        this.changeImportState(this.$scope.currentProgressStep)
    }

    private changeImportState(currentImportProgressStep: OpImportProgressStep): void {
        const currentImportState: Enums.ImportStatus = currentImportProgressStep ? currentImportProgressStep.importStatus : Enums.ImportStatus.Unknown

        switch (currentImportState) {
            case Enums.ImportStatus.InProgress: {
                this.$scope.isCanceled = false
                this.$scope.isValidated = true
                this.$scope.isStarted = true
                this.$scope.isSuccedded = false

                break
            }
            case Enums.ImportStatus.Succeded: {
                this.$scope.isCanceled = false
                this.$scope.isValidated = false
                this.$scope.isStarted = false
                this.$scope.isSuccedded = true

                break
            }
            case Enums.ImportStatus.Validated: {
                this.$scope.isCanceled = false
                this.$scope.isValidated = true
                this.$scope.isStarted = false
                this.$scope.isSuccedded = false

                break
            }
            case Enums.ImportStatus.Cancelled: {
                this.$scope.isCanceled = true
                this.$scope.isValidated = true
                this.$scope.isStarted = false
                this.$scope.isSuccedded = false

                break
            }
            case Enums.ImportStatus.Loaded: {
                this.$scope.isCanceled = false
                this.$scope.isValidated = false
                this.$scope.isStarted = false
                this.$scope.isSuccedded = false
                this.$scope.isLoaded = true

                break
            }
            case Enums.ImportStatus.NotValid:
            case Enums.ImportStatus.Failed:
            case Enums.ImportStatus.NotValid:
            case Enums.ImportStatus.Unknown:
            default: {
                this.$scope.isCanceled = false
                this.$scope.isValidated = false
                this.$scope.isStarted = false
                this.$scope.isSuccedded = false
                this.$scope.isLoaded = false

                break
            }
        }
    }

    private confirmBackgroundImport(): void {
        this.isFromBackgroundPopover = true
        this.isBackgroundImport = true

        this.closeImportPopup()
    }

    private declineBackgroundImport(): void {
        this.isFromBackgroundPopover = true
        this.isBackgroundImport = false

        this.closeImportPopup()
    }

    private startRefreshTimer(): void {
        this.stopRefreshTimer()

        this.timeout = setTimeout(() => { this.importProgress() }, ImportController.REFRESH_PERIOD)
    }

    private stopRefreshTimer(): void {
        if (this.timeout != null) {
            clearTimeout(this.timeout)
        }
    }

    // Import Control End


    createGrid = (importData: OpImportData, dataTypesInGroup: OpDataTypeInGroup[], headerDataTypeDict: Dictionary<OpDataType>) => {
        this.importGridDataSource = []

        this.importGridDataSource = this.prepareDataSource(importData, dataTypesInGroup, this.importGridDataSource, headerDataTypeDict)

        const detailsRowsVisibleState: boolean = this.importService.loadDetailsRowsVisibleState()
        this.$scope.dataSource = new DataSource({
            store: this.importGridDataSource
        })

        this.$scope.gridOptions = {
            addCustomFilters: true,
            allowColumnReordering: !this.$scope.options.columnsReorderingDisabled,
            columns: this.importGridService.getGridColumns(dataTypesInGroup),
            dataSource: this.$scope.dataSource,
            idReferenceType: this.selectedPlugin.type,
            ignoreKey: true,
            name: 'ImportGrid',
            type: GridTypes.PAGINATED_SMALL,

            buttonsOptions: {
                enabled: true,
                deleteRows: false, //Lukasz said to hide this batton
                toggleDetailsRowsButtonVisible: true,

                onToggleDetailsRows: (isExpanded: boolean): void => {
                    this.importService.saveDetailsRowsVisibleState(isExpanded)
                }
            },

            masterDetail: {
                enabled: true,
                template: "errors",
                autoExpandAll: detailsRowsVisibleState
            },

            onCellPrepared: e => { this.onGridCellPrepared(e) },
            onInitialized: e => { this.onGridInitialized(e) },
            onRowPrepared: e => { this.onGridRowPrepared(e) },
        }
    }

    prepareDataSource = (importData: OpImportData, headerColumns: OpDataTypeInGroup[], importGridDataSource: any[], headerDataTypeDict: Dictionary<OpDataType>): any[] => {
        let dataSource: any[] = importGridDataSource

        if (!dataSource) {
            dataSource = []
        }

        if (importData != null && importData.data != null && importData.headers != null && headerColumns != null) {
            const headerColumnMapping: Dictionary<number> = new Dictionary<number>(null)

            headerColumnMapping.setKeyType(KeyType.Number)

            for (let i = 0; i < importData.headers.length; i++) {
                if (headerDataTypeDict.containsKey(importData.headers[i])) {
                    const dataType: OpDataType = headerDataTypeDict.tryGetValue(importData.headers[i])

                    if (headerColumns.some((column) => { return column.idDataType == dataType.idDataType })) {
                        headerColumnMapping.addOrUpdate(dataType.idDataType, i)
                    }
                }
            }

            importData.data.forEach((row, index) => {
                let dataRow: any = {}
                let existingRow: boolean = dataSource.length > index && dataSource[index]

                if (existingRow) {
                    dataRow = dataSource[index]
                }

                headerColumns.forEach(column => {
                    if (column.dataType.idDataTypeClass === DataTypeClass.Boolean) {
                        const val: number = row[headerColumnMapping.tryGetValue(column.idDataType)]
                        let value = false

                        if (val && val.toString().toLowerCase() === 'true') {
                            value = true
                        }

                        dataRow[column.idDataType] = value
                    } else {
                        dataRow[column.idDataType] = row[headerColumnMapping.tryGetValue(column.idDataType)]
                    }
                })

                let dataRowStatus: OpImportItemSummary = row[ImportController.SUMMARY_MASTER_DETAIL_NAME]

                if (dataRowStatus) {
                    if (dataRowStatus.importStatus === Enums.ImportStatus.Succeded) {
                        dataRow.success = true
                    } else if (dataRowStatus.importStatus === Enums.ImportStatus.NotValid || dataRowStatus.importStatus === Enums.ImportStatus.Failed) {
                        dataRow.error = true
                    } else if (dataRowStatus.importStatus === Enums.ImportStatus.Skipped) {
                        dataRow.skipped = true
                    }

                    if (dataRowStatus.description && dataRowStatus.description.length > 0) {
                        dataRow.errorInfo = dataRowStatus.description.split('\r\n')
                    }
                }

                if (!dataRow.id) {
                    dataRow.id = index
                }

                if (!existingRow) {
                    dataSource.push(dataRow)
                }
            })
        }
        return dataSource
    }

    showPopup = () => {
        this.$scope.popoverVisible = false
        this.$scope.importPopupVisible = false
        this.$scope.gridPopupVisible = true
    }



    getSubmit = (): ng.IPromise<any> => {
        const deferred: ng.IDeferred<boolean> = this.$q.defer()
        deferred.resolve(true)
        return deferred.promise
    }


    // Progress Bar

    private updateProgressBar(value: number): void {
        if (this.progressBar != null) {
            this.progressBar.option('value', value)

            // Calling repaint in order to force a call of statusFormat. (I isn't called when setting the same values).
            this.progressBar.repaint()
        }
    }

    private onProgressBarInitialized(e: any): void {
        this.progressBar = e.component
    }

    private onProgressBarStatusFormat(ratio: number, value: number): string {
        let currentProgressStep: string = TypeHelper.emptyString

        if (this.$scope.currentProgressStep) {
            currentProgressStep = ` (${this.$scope.currentProgressStep.proceedItemsCount}/${this.$scope.currentProgressStep.itemsCount})`
        }

        return `${this.getLocalizedText(this.localizationText.Progress)}: ${Math.round(ratio * 100)}%${currentProgressStep}`
    }

    private setProgressBarOptions(): void {
        this.$scope.importProgressOptions = {
            visible: true,
            bindingOptions: {
                value: 'uploadedPercent'
            },
            value: 0,
            min: 0,
            max: 100,

            statusFormat: (ratio: number, value: number) => this.onProgressBarStatusFormat(ratio, value),

            onInitialized: (e: any) => this.onProgressBarInitialized(e)
        }
    }

    // Progress Bar End


    // Buttons

    private setButtonsOptions(): void {
        this.$scope.initButtonOptions = {
            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Validate),
            bindingOptions: {
                disabled: 'isInitButtonDisabled()'
            },

            onClick: () => {
                if (
                    this.selectedPlugin != null
                    && _.size(this.selectedPlugin.params) > 0
                    && this.importService.getImportInputParamsMode() === Enums.ImportInputParamsMode.BeforeValidation
                ) {
                    this.initializeAndValidateImport()
                } else {
                    this.validateImport()
                }
            }
        }

        this.$scope.startButtonOptions = {
            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.StartProcess),
            bindingOptions: {
                disabled: 'isStartButtonDisabled()'
            },

            onClick: () => { this.startImport() }
        }

        this.$scope.startImportWithoutExtensionOptions = {
            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.StartProcess),
            bindingOptions: {
                disabled: false
            },

            onClick: () => {
                this.initImport(null, null, Enums.ImportInputParamsMode.BeforeInitialization)
            }
        }

        this.$scope.stopButtonOptions = {
            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Stop),
            bindingOptions: {
                disabled: 'isStopButtonDisabled()'
            },

            onClick: () => { this.openStopImportConfirmationPopup() }
        }

        this.$scope.finishButtonOptions = {
            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Close),

            onClick: () => this.closeImportPopup()
        }
    }

    // Buttons End


    // Confirmations

    private openResumeImportConfirmationPopup(): void {
        //TO DO!!! -pierwsze otwrcie popupa nie działa, nie wywałuje się construktor, przez co nie ma jeszcze odbiorcy eventa - sprawdza AMałyszko
        this.$timeout(() => {//Wygenerowanie kontrolki ConfirmationPopup, bez zagnieżdzonego Timeout'a Event nie zostanie odebrany, bo nie zdąży się stworzyć kontrolka, która ma go odebrać
            this.$timeout(() => {
                const confirmationOptions: ConfirmationOptions = {
                    title: i18n('Popup', 'Confirm'),
                    question: i18n('Popup', 'ImportIsInProgress'),

                    onConfirmed: () => this.importResume(),
                }

                this.$scope.$broadcast(EventsNames.OPEN_CONFIRMATION_POPUP + this.$scope.confirmationPopupId, confirmationOptions)
            })
        })
    }

    private openStopImportConfirmationPopup(): void {
        this.$timeout(() => {
            const confirmationOptions: ConfirmationOptions = {
                title: i18n('Popup', 'Confirm'),
                question: i18n('Popup', 'ConfirmCancelImport'),

                onConfirmed: () => this.stopImport(true),
            }

            this.$scope.$broadcast(EventsNames.OPEN_CONFIRMATION_POPUP + this.$scope.confirmationPopupId, confirmationOptions)
        })
    }

    private openBackgroundImportConfirmationPopup(): void {
        this.$timeout(() => {
            const confirmationOptions: ConfirmationOptions = {
                title: i18n('Popup', 'Warning'),
                question: i18n('Popup', 'ConfirmRouteImportCancel'),

                onConfirmed: () => this.confirmBackgroundImport(),
                onDeclined: () => this.declineBackgroundImport(),
            }

            this.$scope.$broadcast(EventsNames.OPEN_CONFIRMATION_POPUP + this.$scope.confirmationPopupId, confirmationOptions)
        })
    }

    // Confirmations End


    // Summary Toast

    private onSummaryToastInitialized(e: any): void {
        this.$scope.summaryToast = e.component
    }

    private setSummaryToastOptions(): void {
        this.$scope.summaryToastOptions = {
            contentTemplate: 'IMPORT_SUMMARY_TEMPLATE',
            displayTime: 5000,
            position: 'bottom center',
            type: 'info',
            visible: false,
            width: 500,
            onInitialized: (e: any) => { this.onSummaryToastInitialized(e) }
        }
    }

    // Summary Toast End


    // Configuration Popover

    private disableConfigurationPopover(): void {
        this.$scope.fileUploaderVisible = true
        this.$scope.fileUploaderOptions.disabled = true
    }

    private enableConfigurationPopover(): void {
        this.$scope.fileUploaderVisible = true
        this.$scope.fileUploaderOptions.disabled = false
    }

    private onConfigurationPopoverInitialized(e: any): void {
        this.$scope.configurationPopover = e.component
    }

    private onConfigurationPopoverShowing(): void {
        this.previousFileMd5 = ""
        this.$scope.fileUploaderOptions.uploaderVisible = true
    }

    private onConfigurationPopoverHiding(): void {
        this.$scope.popoverVisible = false
        this.$scope.fileUploaderVisible = false
    }

    private onFileUploaderReadEnd(reader: FileReader, fileName: string): void {
        this.initImport(
            reader,
            fileName,
            Enums.ImportInputParamsMode.BeforeInitialization
        )
    }

    private onFileUploaderSubmitClick(): void {
        this.$scope.gridOptions = null
    }

    private onPluginSelectionChanged(e: any): void {
        this.$scope.fileUploaderVisible = false
        this.selectedPlugin = e.selectedItem

        if (e.selectedItem.allowedFileExtension == null || e.selectedItem.allowedFileExtension.length == 0) {
            this.$scope.isExtensionRequired = false
        } else {
            this.$scope.isExtensionRequired = true
        }

        this.disableConfigurationPopover()
        this.isPreviousImportCompleted()

        this.$scope.popupName = this.$scope.options.popupName +
            ((e.selectedItem.allowedFileExtension == null || e.selectedItem.allowedFileExtension.length == 0) ? "" : (' (' + e.selectedItem.allowedFileExtension.join(', ') + ')'));
        this.$scope.fileUploaderOptions.acceptValue = CommonHelper.fileExtensionsToMediaTypes(e.selectedItem.allowedFileExtension)
    }

    private setPluginFileUploaderPopupOptions(): void {
        this.$scope.configurationPopoverOptions = {
            width: (this.selectedPlugin.allowedFileExtension == null || this.selectedPlugin.allowedFileExtension.length == 0) ? 250 : 450,
            height: (this.selectedPlugin.allowedFileExtension == null || this.selectedPlugin.allowedFileExtension.length == 0) ? 150 : 300,
            target: '#importButton' + this.$scope.gridId,
            showTitle: true,

            bindingOptions: {
                visible: 'popoverVisible',
                title: 'popupName'
            },

            onHiding: () => { this.onConfigurationPopoverHiding() },
            onShowing: () => { this.onConfigurationPopoverShowing() },
            onInitialized: (e) => { this.onConfigurationPopoverInitialized(e) }
        }
    }

    private setPluginFileUploaderOptions(): void {
        this.$scope.fileUploaderOptions = <FileUploaderOptions>{
            acceptValue: this.selectedPlugin && this.selectedPlugin.allowedFileExtension && this.selectedPlugin.allowedFileExtension.length > 0 ? CommonHelper.fileExtensionsToMediaTypes(this.selectedPlugin.allowedFileExtension) : null,
            uploaderVisible: true,
            customButtonOptions: this.getTemplateButton(),
            resetAfterReadEnd: true,
            submitButtonDisabled: true,

            onReadEnd: (reader: FileReader, fileName: string) => { this.onFileUploaderReadEnd(reader, fileName) },
            onSubmitClick: () => { this.onFileUploaderSubmitClick() },
        }

        this.$scope.pluginSelectBoxOptions = {
            placeholder: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Select),
            dataSource: this.$scope.filteredPlugins,
            displayExpr: 'toString',
            value: this.$scope.filteredPlugins[0],

            onSelectionChanged: (e: any) => { this.onPluginSelectionChanged(e) }
        }
    }

    // Configuration Popover End


    // Params Popup

    private createOpImportData(fileReader: FileReader, fileName: string, pluginParams: OpImportPluginParam[]): OpImportData {
        const fileToImport: OpFile = new OpFile()
        const dataToImport: OpImportData = new OpImportData()

        if (fileName != null) {
            fileToImport.physicalFileName = fileName
            fileToImport.fileBytes = (<string>fileReader.result).split(",")[1]

            dataToImport.fileName = fileName
            dataToImport.importFile = fileToImport
        }

        if (pluginParams != null) {
            dataToImport.params = pluginParams
        }

        return dataToImport
    }

    private onPluginParamsSubmit(fields: FieldNameValueModel[], id: number): ng.IPromise<any> {

        if (
            this.selectedPlugin != null
            && _.size(this.selectedPlugin.params) > 0
            && this.importService.getImportInputParamsMode() === Enums.ImportInputParamsMode.BeforeValidation
        ) {
            this.$scope.needHideGrid = true
            this.$scope.gridOptions = null
        }

        this.$scope.$broadcast(EventsNames.CLOSE_CREATE_EDIT_POPUP, true)
        this.$scope.$root.$broadcast(EventsNames.HIDE_LOADING_INDICATOR)

        this.importParamsDeferred.resolve(this.getImportParamsFromFieldModel(fields))

        return this.getSubmit()
    }

    private onPluginParamsClose(): void {
        this.enableConfigurationPopover()

        this.importParamsDeferred.reject()
    }

    private getImportParamsFromFieldModel(fields: FieldNameValueModel[]): OpImportPluginParam[] {
        return fields.map((field: FieldNameValueModel) => {
            const params: OpImportPluginParam[] = this.selectedPlugin ? this.selectedPlugin.params : []
            const foundParam: OpImportPluginParam = _.find(params, param => param.idDataType === field.idDataType)
            const param: OpImportPluginParam = new OpImportPluginParam(null)

            param.idDataType = field.idDataType
            param.value = field.value
            param.isVisible = true
            param.isInputParam = true
            param.defaultValue = foundParam ? foundParam.defaultValue : null

            return param
        })
    }

    private getDataTypeInGroupFromImportParams(pluginParams: OpImportPluginParam[]): JQueryPromise<OpDataTypeInGroup[]> {
        const paramIdDataTypes: number[] = pluginParams.map((pluginParam: OpImportPluginParam) => pluginParam.idDataType)

        return $
            .when(
                this.userSession
                    .getDataProvider()
                    .DataTypeGroup
                    .getDataTypeInGroup(Enums.DataTypeGroup.WEBSIMAX_IMPORT_PLUGIN_PARAMS, Enums.ReferenceType.IdDistributor, this.userSession.getLoggedOperator().idDistributor),
                this.userSession
                    .getDataProvider()
                    .DataType
                    .getDataTypes(paramIdDataTypes)
                    .then((paramsDataTypes: OpDataType[]) => {
                        if (paramsDataTypes != null && paramsDataTypes.length > 0) {
                            return paramsDataTypes.map((dataType: OpDataType) => {
                                // Cloning datatypes so as to set different default values.
                                const dataTypeClone = OpDataType.restore(dataType)
                                const pluginParam: OpImportPluginParam = _.find(
                                    this.selectedPlugin.params,
                                    (pluginParam: OpImportPluginParam) => pluginParam.idDataType === dataTypeClone.idDataType
                                )

                                dataTypeClone.defaultValue = pluginParam.defaultValue

                                return this.dataTypeService.dataTypeToDataTypeInGroup(dataTypeClone)
                            })
                        }

                        return null
                    })
            )
            .then((descrDataTypeInGroup: OpDataTypeInGroup[], parametersDataTypeInGroup: OpDataTypeInGroup[]) => {
                if (descrDataTypeInGroup != null && descrDataTypeInGroup.length > 0 && parametersDataTypeInGroup != null && parametersDataTypeInGroup.length > 0) {
                    parametersDataTypeInGroup.forEach((parameter: OpDataTypeInGroup) => {
                        const dtigDescr: OpDataTypeInGroup = _.find(descrDataTypeInGroup, (dtigDescr: OpDataTypeInGroup) => parameter.idDataType == dtigDescr.idDataType && dtigDescr.descr != null)
                        if (dtigDescr != null && parameter.dataType != null) {
                            parameter.dataType.descr = dtigDescr.descr
                        }
                    })
                }

                return parametersDataTypeInGroup
            })
    }

    private getCreateEditOptionsFromDataTypeInGroups(dataTypeInGroup: OpDataTypeInGroup[]): CreateEditInitParams {
        const section: CreateEditSection = {
            name: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Basics),
            type: CommonCreateEdit.ReferenceObjectBaseParams,
            dataTypesToEdit: dataTypeInGroup,
            dataTypesRequired: dataTypeInGroup
        }

        return {
            moduleName: this.$scope.options.moduleName,
            idReferenceType: this.selectedPlugin.type,
            createEditSelectedTabType: Enums.SimaxCreateEditSelectedTabType.BaseParameters,
            groupReferenceType: null,
            groupReferenceValue: null,
            referenceObjectId: null,
            referenceObject: null,
            sections: [section],
            useBusinessObjects: true
        }
    }

    private showParamsPopup(createEditOptions: CreateEditInitParams): void {
        this.$scope.$broadcast(EventsNames.SHOW_CREATE_EDIT_POPUP)
        this.$scope.$broadcast(EventsNames.OPEN_CREATE_EDIT_POPUP, createEditOptions)
    }

    private setCreateEditImportOptions(): void {
        this.$scope.paramsOptions = {
            localStorageVarForSavedEntity: '',
            referenceType: this.$scope.options.referenceType,
            succsessMessage: '',
            title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ImportParameters),
            type: '',

            onSave: null,
            onBulkSave: null,

            onClose: () => { this.onPluginParamsClose() },
            onSubmit: (fields: FieldNameValueModel[], id: number) => { return this.onPluginParamsSubmit(fields, id) },

            submitBulkEdit: () => { return this.getSubmit() },
        }
    }

    // Params Popup End


    //#region Template

    private getTemplate(): void {
        this.userSession
            .getDataProvider()
            .System
            .importGetTemplate(this.selectedPlugin.type, null, this.selectedPlugin.name)
            .then((templateFile: OpFile) => {
                this.saveFile(templateFile)
            })
            .fail((error: any) => {
                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)

                ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))

                console.warn(ImportController.LOG_STRING, `Unable to download template. Error: ${errorMsg}.`)

            })
    }

    private saveFile(file: OpFile): void {
        if (file != null && file.fileBytes != null) {
            FileComponent.OpenFileSaver(file)
        } else {
            ToastHelper.showError(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoTemplateToLoad))

            console.warn(ImportController.LOG_STRING, "Template is null or empty.")
        }
    }

    private getTemplateButton(): any {
        let getTemplateButtonOptions: DevExpress.ui.dxButtonOptions = {
            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.DownloadTemplate),
            bindingOptions: {
                disabled: false
            },

            onClick: () => { this.getTemplate() }
        }

        return getTemplateButtonOptions
    }

    //#endregion


    //#region Errors Summary

    private showErrorsSummarySection(): void {
        this.$scope.errorsSummaryVisible = true
    }

    private hideErrorsSummarySection(): void {
        this.$scope.errorsSummaryVisible = false
    }

    private initializeErrorsSummary(): JQueryPromise<void> {
        this.$scope.errorsSummaryOptions = {
            onInitialized: (errorsSummary: IIMRErrorsSummary) => this.errorsSummaryComponent = errorsSummary,
            onSelectErrorRows: (keys: number[]): void => { this.filterGrid(keys) }
        }

        return TypeHelper.onInitializedAll(this.$scope.errorsSummaryOptions)
    }

    private filterGrid(keys: number[]): void {
        const filterExpr: ((string | number)[] | string)[] = []
        keys.forEach((key: number, index: number) => {
            filterExpr.push(["id", key])

            if (index !== keys.length - 1) {
                filterExpr.push("or")
            }
        })

        if (filterExpr.length > 0) {
            this.$scope.dataSource.filter(filterExpr)
            this.$scope.dataSource.load()
        } else {
            this.$scope.dataGrid.clearFilter()
        }
    }

    private setSummaryOptions(summaryOptions: SummaryOptions): void {
        if (this.errorsSummaryComponent != null) {
            this.errorsSummaryComponent.setSummaryOptions(summaryOptions)
        }
    }

    //#endregion
}